import { Box, Typography } from '@material-ui/core';
import type { LaunchPadData } from '@mogul/api-client';
import { LaunchPadInner } from '@mogul/shared';
import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';

import { launchPadApi } from '../../../api';
import { ConnectWalletModal } from '../../../components/common/modal';
import { SetMintingModal } from '../../../components/common/modal/MintingModal.component';
import { SignupModal } from '../../../components/common/modal/SignupDropNotifyModal.component';
import { WrongNetworkModal } from '../../../components/common/modal/WrongNetworkModal.component';
import { ModalStateContext } from '../../../contexts/app-state-context/modal-state.context';
import { useWeb3State } from '../../../hooks';
import { useCloudfrontLaunchpad } from '../../../hooks/cloudfront-hook';
import {
  useERC1155MintableLaunchpadContract,
  useLaunchpadContract,
} from '../../../hooks/launchpad-contract.hooks';

export const LaunchPad = () => {
  const history = useHistory();
  const [mintedAmount, setMintedAmount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [launchPadData, setLaunchPadData] =
    useState<LaunchPadData | undefined>();
  const { setModal } = useContext(ModalStateContext);
  const { walletDetails } = useWeb3State();
  const { collectionSlug: collectionSlugCheckSum } =
    useParams<{ collectionSlug: string }>();

  const { contractInitialized, totalSupply } = useLaunchpadContract({
    launchPadContractAddress:
      launchPadData?.collection?.contractAddress != null
        ? launchPadData.collection.contractAddress
        : '',
  });

  const { ERC1155ContractInitialized, getTotalSupplies } =
    useERC1155MintableLaunchpadContract({
      launchPadContractAddress:
        launchPadData?.collection?.contractAddress ?? '',
    });

  useCloudfrontLaunchpad(launchPadData);

  useEffect(() => {
    if (
      !contractInitialized ||
      !location.href.startsWith('https://film3.mogulproductions.com/')
    ) {
      return;
    }

    void (async () => {
      const { tokenType } = launchPadData?.collection ?? {
        tokenType: '',
        maxSupply: 0,
      };

      if (tokenType === 'ERC1155 Mintable') {
        return;
      }

      const newTotalSupply = await totalSupply();

      setMintedAmount(newTotalSupply ? newTotalSupply.toNumber() : 0);
    })();
  }, [contractInitialized, walletDetails, launchPadData]);

  useEffect(() => {
    if (!ERC1155ContractInitialized) {
      return;
    }

    void (async () => {
      const { tokenType, maxSupply } = launchPadData?.collection ?? {
        tokenType: '',
        maxSupply: 0,
      };
      if (
        tokenType === 'ERC1155 Mintable' &&
        maxSupply !== null &&
        maxSupply !== undefined
      ) {
        // We grab the supply limit we have recorded in the database and take a sample of the the first 100 tokens
        const lookupRange = 100;
        const sampleLimit =
          parseFloat(maxSupply) < lookupRange
            ? parseInt(`${maxSupply}`, 10)
            : lookupRange;

        const { supplies, lastMintedTokenId } = await getTotalSupplies(
          0,
          sampleLimit,
        );

        let additionalSupply = 0;
        // We create a two dimensional array to organize our supply calls
        // We can't make a single request for all at once in case there are too many to make the request succeed
        if (lastMintedTokenId > sampleLimit) {
          // We loop through the remaining ids we haven't covered
          const promises = [];
          for (
            let x = sampleLimit + 1;
            x <= parseInt(`${lastMintedTokenId}`, 10);
            x += lookupRange
          ) {
            promises.push(getTotalSupplies(x, x + lookupRange - 1));
          }

          const responses = await Promise.all(promises);
          responses.forEach((response: { supplies: number[] }) => {
            const subtotal = response.supplies.reduce((sup, a) => sup + a, 0);
            additionalSupply += subtotal;
          });
        }

        // We now get total Supply
        const newTotalSupply = supplies.reduce((sup, a) => sup + a, 0);

        setMintedAmount(newTotalSupply + additionalSupply);
        // let supplyList = await totalSupply([]);
      }
    })();
  }, [ERC1155ContractInitialized, walletDetails, launchPadData]);

  useEffect(() => {
    const getLaunchPadInfo = async () => {
      setLoading(true);
      const launchPadInfo = await launchPadApi.getLaunchPadFromCollectionSlug({
        slug: collectionSlugCheckSum,
      });
      setLaunchPadData(launchPadInfo.data.data);

      setLoading(false);
    };
    void getLaunchPadInfo();
  }, [collectionSlugCheckSum]);

  if (!loading && !launchPadData) {
    return (
      <Box className="w-full h-full items-center justify-center flex flex-col flex-wrap text-center">
        <Typography variant="h1">The launchpad could not be found!</Typography>
      </Box>
    );
  }

  return (
    <LaunchPadInner
      launchPadData={launchPadData}
      mintedAmount={mintedAmount}
      loading={loading}
      MetaMaskModal={ConnectWalletModal}
      SetMintingModal={SetMintingModal}
      SignupModal={SignupModal}
      WrongNetworkModal={WrongNetworkModal}
      history={history}
      walletInfo={walletDetails}
      setModal={setModal}
    />
  );
};
